import type {
  EventResponse,
  EventLifecycleState,
  CommitmentResponse,
  PurchaseResponse,
  InterestRung,
  InterestSource,
} from '@@/types/api';
import type { EventViewerRole } from '@@/types/api/generated/event-viewer-role';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Frontend-derived viewer role (identical to the generated EventViewerRole) */
export type ViewerRole = EventViewerRole;

/** Input for role derivation */
export interface ViewerContext {
  isAuthenticated: boolean;
  apiViewerRole: EventViewerRole | undefined;
  commitment: CommitmentResponse | null;
  purchase: PurchaseResponse | null;
}

/** CTA action identifier */
export type CtaAction = 'login' | 'commit' | 'purchase' | 'withdraw' | 'none';

/** CTA descriptor consumed by the CTA button component (Phase 3.3) */
export interface CtaDescriptor {
  label: string;
  action: CtaAction;
  disabled: boolean;
  disabledReason: string | null;
  variant: 'primary' | 'secondary' | 'outlined' | 'text';
}

/**
 * Descriptor for the interest control (Interested / Going toggle) rendered on
 * event cards and detail surfaces. Consumed by the Phase 3 interest UI.
 *
 * Native interest is "Interested"-only — `options` is ALWAYS `['interested']`
 * and "Going" is never a native declared rung (it is derived from an active
 * commitment). Almanac interest supports both rungs.
 */
export interface InterestControlDescriptor {
  source: InterestSource;
  visible: boolean;
  rung: InterestRung | null; // current selection to render
  options: InterestRung[]; // native: ['interested']; almanac: ['interested','going']
  action: 'toggle' | 'login' | 'none';
  disabled: boolean;
  disabledReason: string | null;
}

/** Funding display mode */
export type FundingDisplayMode =
  | 'fundraising'
  | 'finalization'
  | 'post_confirmation'
  | 'failed'
  | 'canceled'
  | 'pre_fundraising'
  | 'hidden';

/** Threshold display type: 'registrations' (commitments), 'attendees' (party seats), 'cash' (revenue) */
export type ThresholdDisplayType = 'registrations' | 'attendees' | 'cash';

/** Funding display descriptor consumed by EventFundingModule */
export interface FundingDisplayDescriptor {
  mode: FundingDisplayMode;
  thresholdType: ThresholdDisplayType;
  progressPercentage: number;
  timeRemaining: string | null;
  fundraisingDeadline: string | null;
  timezone: string | null;
  threshold: number;
  committedCount: number | null;
  paidCount: number | null;
  finalizationCount: number | null;
  isSoldOut: boolean;
  isGoalMet: boolean;
  message: string | null;
}

/** Lifecycle transition action */
export type TransitionAction =
  | 'submit_for_review'
  | 'start_fundraising'
  | 'cancel';

/** Descriptor for an available lifecycle transition */
export interface TransitionDescriptor {
  action: TransitionAction;
  label: string;
  variant: 'primary' | 'secondary' | 'error';
  icon: string;
  requiresConfirmation: boolean;
  confirmationMessage?: string;
}

/** Fields that may be editable by an organizer */
export type EditableField =
  | 'title'
  | 'description'
  | 'short_description'
  | 'cover_image'
  | 'location'
  | 'timezone'
  | 'start_datetime'
  | 'end_datetime'
  | 'fundraising_deadline'
  | 'pricing'
  | 'ticket_threshold'
  | 'cash_threshold'
  | 'max_tickets'
  | 'max_attendees'
  | 'registration_types'
  | 'rules'
  | 'visibility'
  | 'ticket_sales_close_datetime';

// ---------------------------------------------------------------------------
// Active commitment statuses (user has a live stake in the event)
// ---------------------------------------------------------------------------

const ACTIVE_COMMITMENT_STATUSES = new Set([
  'committed',
  'payment_pending',
  'paid',
]);

// ---------------------------------------------------------------------------
// deriveViewerRole
// ---------------------------------------------------------------------------

export function deriveViewerRole(ctx: ViewerContext): ViewerRole {
  if (ctx.apiViewerRole === 'organizer') return 'organizer';
  if (ctx.apiViewerRole === 'attendee') return 'attendee';
  if (!ctx.isAuthenticated) return 'visitor';

  if (ctx.purchase && ctx.purchase.status === 'paid') return 'attendee';
  if (ctx.commitment && ACTIVE_COMMITMENT_STATUSES.has(ctx.commitment.status))
    return 'backer';

  return 'user';
}

// ---------------------------------------------------------------------------
// isFinalizing
// ---------------------------------------------------------------------------

/**
 * Whether the event is in the fundraising finalization window (payments for
 * committed backers are being captured; commit/withdraw/switch-type are
 * rejected by the backend).
 *
 * The backend signals this with `user_facing_status: 'Processing payments'`
 * (plus `is_finalization_active: true` on the detail resource). The legacy
 * `'Fundraising'` + `is_finalization_active` combination is still honoured so
 * either shape routes through the same finalization UI. Every finalization
 * check must go through this predicate — never inline the status comparison.
 */
export function isFinalizing(
  event: Pick<EventResponse, 'user_facing_status' | 'is_finalization_active'>,
): boolean {
  const status = event.user_facing_status;
  return (
    status === 'Processing payments' ||
    (status === 'Fundraising' && (event.is_finalization_active ?? false))
  );
}

/**
 * Whether the event is in the open fundraising phase — `'Fundraising'` and
 * not finalizing. This is the only window in which commit, withdraw,
 * switch-type and party-size changes are allowed.
 */
export function isOpenFundraising(
  event: Pick<EventResponse, 'user_facing_status' | 'is_finalization_active'>,
): boolean {
  return event.user_facing_status === 'Fundraising' && !isFinalizing(event);
}

// ---------------------------------------------------------------------------
// fundingDisplay
// ---------------------------------------------------------------------------

export function fundingDisplay(event: EventResponse): FundingDisplayDescriptor {
  const counts = event.threshold_counts;
  const thresholdType: ThresholdDisplayType =
    counts === 'cash'
      ? 'cash'
      : counts === 'attendees'
        ? 'attendees'
        : 'registrations';

  const committedCount =
    thresholdType === 'cash'
      ? (event.committed_cash_value ?? null)
      : thresholdType === 'attendees'
        ? (event.committed_attendee_count ?? null)
        : (event.committed_ticket_count ?? null);

  const threshold =
    thresholdType === 'cash'
      ? (event.cash_threshold ?? 0)
      : event.ticket_threshold;

  const base: Pick<
    FundingDisplayDescriptor,
    | 'thresholdType'
    | 'progressPercentage'
    | 'threshold'
    | 'isSoldOut'
    | 'isGoalMet'
  > = {
    thresholdType,
    progressPercentage: event.progress_percentage ?? 0,
    threshold,
    isSoldOut: event.is_sold_out ?? false,
    isGoalMet:
      (committedCount != null &&
        threshold > 0 &&
        committedCount >= threshold) ||
      (event.progress_percentage ?? 0) >= 100,
  };

  const status = event.user_facing_status;

  if (isFinalizing(event)) {
    return {
      ...base,
      mode: 'finalization',
      timeRemaining: null,
      fundraisingDeadline: null,
      timezone: null,
      committedCount,
      paidCount: null,
      finalizationCount: event.finalization_progress_ticket_count ?? null,
      message: null,
    };
  }

  if (isOpenFundraising(event)) {
    return {
      ...base,
      mode: 'fundraising',
      timeRemaining: event.time_remaining ?? null,
      fundraisingDeadline: event.fundraising_deadline ?? null,
      timezone: event.timezone ?? null,
      committedCount,
      paidCount: null,
      finalizationCount: null,
      message: null,
    };
  }

  if (
    status === 'Confirmed' ||
    status === 'In Progress' ||
    status === 'Completed'
  ) {
    const paidCount =
      thresholdType === 'cash'
        ? (event.paid_cash_value ?? null)
        : thresholdType === 'attendees'
          ? (event.paid_attendee_count ?? null)
          : (event.paid_ticket_count ?? null);
    return {
      ...base,
      mode: 'post_confirmation',
      timeRemaining: null,
      fundraisingDeadline: null,
      timezone: null,
      committedCount: null,
      paidCount,
      finalizationCount: null,
      message: null,
    };
  }

  if (status === 'Did Not Fund') {
    return {
      ...base,
      mode: 'failed',
      timeRemaining: null,
      fundraisingDeadline: null,
      timezone: null,
      committedCount: null,
      paidCount: null,
      finalizationCount: null,
      message: 'This event did not reach its funding goal',
    };
  }

  if (status === 'Canceled') {
    return {
      ...base,
      mode: 'canceled',
      timeRemaining: null,
      fundraisingDeadline: null,
      timezone: null,
      committedCount: null,
      paidCount: null,
      finalizationCount: null,
      message: 'This event has been canceled',
    };
  }

  if (
    status === 'Draft' ||
    status === 'Pending Review' ||
    status === 'Approved'
  ) {
    return {
      ...base,
      mode: 'pre_fundraising',
      timeRemaining: null,
      fundraisingDeadline: null,
      timezone: null,
      committedCount: null,
      paidCount: null,
      finalizationCount: null,
      message:
        status === 'Draft'
          ? 'This event is a draft. Submit your event for review to start fundraising!'
          : "Fundraising hasn't started yet",
    };
  }

  return {
    ...base,
    mode: 'hidden',
    timeRemaining: null,
    fundraisingDeadline: null,
    timezone: null,
    committedCount: null,
    paidCount: null,
    finalizationCount: null,
    message: null,
  };
}

// ---------------------------------------------------------------------------
// isTicketSalesClosed
// ---------------------------------------------------------------------------

export function isTicketSalesClosed(event: EventResponse): boolean {
  const cutoff = event.effective_ticket_sales_close_datetime;
  if (!cutoff) return false;
  return new Date(cutoff) < new Date();
}

// ---------------------------------------------------------------------------
// canCommit / canPurchase
// ---------------------------------------------------------------------------

export function canCommit(event: EventResponse, role: ViewerRole): boolean {
  return (
    role === 'user' && isOpenFundraising(event) && !(event.is_sold_out ?? false)
  );
}

export function canPurchase(event: EventResponse, role: ViewerRole): boolean {
  return (
    role === 'user' &&
    event.user_facing_status === 'Confirmed' &&
    !(event.is_sold_out ?? false) &&
    !isTicketSalesClosed(event)
  );
}

// ---------------------------------------------------------------------------
// ctaFor
// ---------------------------------------------------------------------------

/**
 * CTA for a non-backer (visitor or signed-in user) during the finalization
 * window. Commitments have closed for good: the event will either be confirmed
 * and go on sale at the ticket price, or fail. Shared by the visitor and user
 * branches so the copy lives in one place.
 */
function fundraisingEndedCta(): CtaDescriptor {
  return {
    label: 'Fundraising ended',
    action: 'none',
    disabled: true,
    disabledReason:
      'Results are being finalized. If this event is confirmed, tickets will go on sale at the ticket price.',
    variant: 'outlined',
  };
}

export function ctaFor(event: EventResponse, role: ViewerRole): CtaDescriptor {
  const status = event.user_facing_status;

  // Visitor: finalization check, sold-out check, registration closed check,
  // then login. Finalization comes first to mirror the user branch, so both
  // non-backer roles see the same descriptor during the window.
  if (role === 'visitor') {
    if (isFinalizing(event)) {
      return fundraisingEndedCta();
    }
    if (event.is_sold_out ?? false) {
      return {
        label: 'Sold out',
        action: 'none',
        disabled: true,
        disabledReason: 'This event has reached its maximum capacity',
        variant: 'outlined',
      };
    }
    if (status === 'Confirmed' && isTicketSalesClosed(event)) {
      return {
        label: 'Registration closed',
        action: 'none',
        disabled: true,
        disabledReason: 'Ticket sales have ended',
        variant: 'outlined',
      };
    }
    return {
      label: 'Sign in to participate',
      action: 'login',
      disabled: false,
      disabledReason: null,
      variant: 'primary',
    };
  }

  // Organizer: organizer actions handled by page
  if (role === 'organizer') {
    return {
      label: '',
      action: 'none',
      disabled: true,
      disabledReason: null,
      variant: 'text',
    };
  }

  // Backer (has active commitment)
  if (role === 'backer') {
    if (isOpenFundraising(event)) {
      return {
        label: 'Withdraw commitment',
        action: 'withdraw',
        disabled: false,
        disabledReason: null,
        variant: 'outlined',
      };
    }
    if (isFinalizing(event)) {
      return {
        label: 'Processing payment...',
        action: 'none',
        disabled: true,
        disabledReason: 'Your payment is being processed',
        variant: 'outlined',
      };
    }
    return {
      label: 'Committed',
      action: 'none',
      disabled: true,
      disabledReason: null,
      variant: 'outlined',
    };
  }

  // Attendee (has completed purchase or API role)
  if (role === 'attendee') {
    return {
      label: "You're attending",
      action: 'none',
      disabled: true,
      disabledReason: null,
      variant: 'outlined',
    };
  }

  // User (authenticated, no commitment/purchase)
  if (isOpenFundraising(event)) {
    if (event.is_sold_out ?? false) {
      return {
        label: 'Sold out',
        action: 'none',
        disabled: true,
        disabledReason: 'This event has reached its maximum capacity',
        variant: 'outlined',
      };
    }
    return {
      label: 'Commit to attend',
      action: 'commit',
      disabled: false,
      disabledReason: null,
      variant: 'primary',
    };
  }

  if (isFinalizing(event)) {
    return fundraisingEndedCta();
  }

  if (status === 'Confirmed') {
    if (event.is_sold_out ?? false) {
      return {
        label: 'Sold out',
        action: 'none',
        disabled: true,
        disabledReason: 'This event has reached its maximum capacity',
        variant: 'outlined',
      };
    }
    if (isTicketSalesClosed(event)) {
      return {
        label: 'Registration closed',
        action: 'none',
        disabled: true,
        disabledReason: 'Ticket sales have ended',
        variant: 'outlined',
      };
    }
    return {
      label: 'Purchase ticket',
      action: 'purchase',
      disabled: false,
      disabledReason: null,
      variant: 'primary',
    };
  }

  // Terminal / inactive states
  return {
    label: '',
    action: 'none',
    disabled: true,
    disabledReason: null,
    variant: 'text',
  };
}

// ---------------------------------------------------------------------------
// Interest controls
// ---------------------------------------------------------------------------

/**
 * Interest control for a NATIVE community event.
 *
 * Native interest is "Interested"-only: `options` is always `['interested']`
 * and "Going" is never offered here — it is derived from an active commitment.
 * Once a viewer has committed/paid (role backer/attendee/organizer) the control
 * disappears entirely, because the commit/purchase CTA covers them.
 */
export function nativeInterestControlFor(
  event: EventResponse,
  role: ViewerRole,
  isAuthenticated: boolean,
): InterestControlDescriptor {
  const hidden: InterestControlDescriptor = {
    source: 'native',
    visible: false,
    rung: null,
    options: ['interested'],
    action: 'none',
    disabled: false,
    disabledReason: null,
  };

  // Committed/paid/organizing viewers are served by the commit/purchase CTA.
  if (role === 'organizer' || role === 'backer' || role === 'attendee') {
    return hidden;
  }

  // Concluded/terminal native events move to the past archive — no live control.
  if (event.lifecycle_state && isTerminalState(event.lifecycle_state)) {
    return hidden;
  }

  // Anonymous visitor: prompt sign-in.
  if (!isAuthenticated) {
    return {
      source: 'native',
      visible: true,
      rung: null,
      options: ['interested'],
      action: 'login',
      disabled: false,
      disabledReason: null,
    };
  }

  // Authenticated, not yet committed (role `user`): live toggle.
  return {
    source: 'native',
    visible: true,
    rung: event.my_rung ?? null,
    options: ['interested'],
    action: 'toggle',
    disabled: false,
    disabledReason: null,
  };
}

/**
 * Interest control for an ALMANAC-sourced informational event.
 *
 * Almanac interest supports both rungs ("Interested" ↔ "Going"), both declared
 * and stored. Concluded Almanac events are excluded entirely (no live control).
 */
export function almanacInterestControlFor(
  isConcluded: boolean,
  isAuthenticated: boolean,
  rung: InterestRung | null,
): InterestControlDescriptor {
  if (isConcluded) {
    return {
      source: 'almanac',
      visible: false,
      rung: null,
      options: ['interested', 'going'],
      action: 'none',
      disabled: false,
      disabledReason: null,
    };
  }

  if (!isAuthenticated) {
    return {
      source: 'almanac',
      visible: true,
      rung: null,
      options: ['interested', 'going'],
      action: 'login',
      disabled: false,
      disabledReason: null,
    };
  }

  return {
    source: 'almanac',
    visible: true,
    rung,
    options: ['interested', 'going'],
    action: 'toggle',
    disabled: false,
    disabledReason: null,
  };
}

/**
 * Interest control for an EVENT IDEA.
 *
 * Ideas are "Interested"-only — `options` is always `['interested']` and there
 * is no "Going" rung (nothing concrete to be going to yet). Unlike native, an
 * idea has no commitment concept, so the control is never suppressed for a
 * committed/backer viewer — it is always visible and live.
 */
export function ideaInterestControlFor(
  isAuthenticated: boolean,
  rung: InterestRung | null,
): InterestControlDescriptor {
  return {
    source: 'idea',
    visible: true,
    rung,
    options: ['interested'],
    action: isAuthenticated ? 'toggle' : 'login',
    disabled: false,
    disabledReason: null,
  };
}

// ---------------------------------------------------------------------------
// isFieldEditable
// ---------------------------------------------------------------------------

const PRE_FUNDRAISING_STATES: Set<EventLifecycleState> = new Set([
  'draft',
  'pending_review',
  'approved',
]);

const LOCKED_AFTER_FUNDRAISING: Set<EditableField> = new Set([
  'pricing',
  'ticket_threshold',
  'cash_threshold',
  'max_tickets',
  'max_attendees',
  'registration_types',
  'start_datetime',
  'end_datetime',
  'fundraising_deadline',
  'location',
  'timezone',
]);

const TERMINAL_STATES: Set<EventLifecycleState> = new Set([
  'completed',
  'failed',
  'canceled',
  'denied',
  'abandoned',
]);

export function isPreFundraisingState(state: EventLifecycleState): boolean {
  return PRE_FUNDRAISING_STATES.has(state);
}

export function isTerminalState(state: EventLifecycleState): boolean {
  return TERMINAL_STATES.has(state);
}

export function isFieldEditable(
  field: EditableField,
  event: EventResponse,
): boolean {
  const state = event.lifecycle_state;
  if (!state) return false;

  if (TERMINAL_STATES.has(state)) return false;

  if (PRE_FUNDRAISING_STATES.has(state)) return true;

  // Fundraising / confirmed / in_progress: planner-critical fields locked
  return !LOCKED_AFTER_FUNDRAISING.has(field);
}

// ---------------------------------------------------------------------------
// canDelete / canArchive / canUnarchive
// ---------------------------------------------------------------------------

export function canDelete(state: EventLifecycleState | undefined): boolean {
  return state === 'draft';
}

export function canArchive(
  state: EventLifecycleState | undefined,
  archivedAt: string | null | undefined,
): boolean {
  if (!state) return false;
  return isTerminalState(state) && !archivedAt;
}

export function canUnarchive(archivedAt: string | null | undefined): boolean {
  return !!archivedAt;
}

// ---------------------------------------------------------------------------
// canViewAttendeeList / canViewAttendeeDetails / canExportAttendees
// ---------------------------------------------------------------------------

export function canViewAttendeeList(role: ViewerRole): boolean {
  return role === 'organizer' || role === 'attendee' || role === 'backer';
}

export function canViewAttendeeDetails(role: ViewerRole): boolean {
  return role === 'organizer';
}

export function canExportAttendees(role: ViewerRole): boolean {
  return role === 'organizer';
}

// ---------------------------------------------------------------------------
// availableTransitions
// ---------------------------------------------------------------------------

export function availableTransitions(
  state: EventLifecycleState | undefined,
): TransitionDescriptor[] {
  if (!state) return [];

  switch (state) {
    case 'draft':
      return [
        {
          action: 'submit_for_review',
          label: 'Submit for Review',
          variant: 'primary',
          icon: 'mdi-send',
          requiresConfirmation: false,
        },
      ];
    case 'approved':
      return [
        {
          action: 'start_fundraising',
          label: 'Start Fundraising',
          variant: 'primary',
          icon: 'mdi-rocket-launch',
          requiresConfirmation: false,
        },
        {
          action: 'cancel',
          label: 'Cancel Event',
          variant: 'error',
          icon: 'mdi-cancel',
          requiresConfirmation: true,
          confirmationMessage:
            'Canceling will forfeit your listing bond. This cannot be undone.',
        },
      ];
    case 'fundraising':
      return [
        {
          action: 'cancel',
          label: 'Cancel Event',
          variant: 'error',
          icon: 'mdi-cancel',
          requiresConfirmation: true,
          confirmationMessage:
            'Canceling will refund all committed attendees. This cannot be undone.',
        },
      ];
    case 'confirmed':
      return [
        {
          action: 'cancel',
          label: 'Cancel Event',
          variant: 'error',
          icon: 'mdi-cancel',
          requiresConfirmation: true,
          confirmationMessage:
            'Canceling will refund all paid attendees. This cannot be undone.',
        },
      ];
    default:
      return [];
  }
}
